import React, { FC, useCallback, useEffect, useRef } from 'react';

import { Formik, useFormikContext } from 'formik';
import { DateTime } from 'luxon';

import { LeoDateInterval } from './LeoDateInterval';

interface PeriodValues {
  period: {
    from?: string;
    to?: string;
  };
}

interface FlatDateIntervalFilterProps {
  startFieldName: string;
  endFieldName: string;
  minDate?: DateTime;
  maxDate?: DateTime;
  width?: number;
  label?: React.ReactNode;
  isRelativeDateDisabled?: boolean;
}

const PeriodSync: FC<{
  from?: string;
  to?: string;
  onChange: (from?: string, to?: string) => void;
}> = ({ from, to, onChange }) => {
  const lastSynced = useRef<{ from?: string; to?: string }>();

  useEffect(() => {
    if (lastSynced.current?.from === from && lastSynced.current?.to === to) return;
    lastSynced.current = { from, to };
    onChange(from, to);
  }, [from, to, onChange]);

  return null;
};

// Bridges LeoDateInterval's {from,to} value shape onto two flat scalar fields
// (e.g. startDate/endDate) on an outer Formik form. Use this when the outer form's
// values are serialized as-is (e.g. into a filter query param) against a backend
// that expects flat bounds rather than a {from,to} pair, so `LeoDateInterval`'s
// nested shape can't live directly on the outer form.
export const FlatDateIntervalFilter: FC<FlatDateIntervalFilterProps> = ({
  startFieldName,
  endFieldName,
  ...leoDateIntervalProps
}) => {
  const { values: outerValues, setFieldValue } =
    useFormikContext<Record<string, string | undefined>>();

  const handlePeriodChange = useCallback(
    (from?: string, to?: string) => {
      setFieldValue(startFieldName, from);
      setFieldValue(endFieldName, to);
    },
    [setFieldValue, startFieldName, endFieldName]
  );

  return (
    <Formik<PeriodValues>
      initialValues={{
        period: { from: outerValues[startFieldName], to: outerValues[endFieldName] },
      }}
      enableReinitialize
      onSubmit={() => undefined}
    >
      {({ values }) => (
        <>
          <LeoDateInterval name="period" {...leoDateIntervalProps} />
          <PeriodSync
            from={values.period?.from}
            to={values.period?.to}
            onChange={handlePeriodChange}
          />
        </>
      )}
    </Formik>
  );
};

export default FlatDateIntervalFilter;
